import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import howIEatService from '../services/howIEatService'
import { FoodPhotoAnalysisResult, HowIEatMealItem } from '../types/howIEat'
import { useTranslation } from '../hooks/use-i18n'

export function mealItemsFromPhotoAnalysis(result: FoodPhotoAnalysisResult): HowIEatMealItem[] {
  return result.items.map(({ name, quantity, unit, calories, proteinG, carbsG, fatsG }) => ({
    name,
    quantity,
    unit: unit || 'g',
    calories,
    proteinG,
    carbsG,
    fatsG,
  }))
}

interface FoodPhotoLogSectionProps {
  onAnalyzed: (items: HowIEatMealItem[], notes?: string, previewUrl?: string) => void
  previewUrl?: string | null
  compact?: boolean
  /** Keep file input wired but hide the card UI (for top action bar). */
  hidden?: boolean
}

export type FoodPhotoLogHandle = {
  openFilePicker: () => void
}

const FoodPhotoLogSection = forwardRef<FoodPhotoLogHandle, FoodPhotoLogSectionProps>(
  ({ onAnalyzed, previewUrl, compact = false, hidden = false }, ref) => {
    const { t, locale } = useTranslation()
    const inputRef = useRef<HTMLInputElement>(null)
    const [isAnalyzing, setIsAnalyzing] = useState(false)
    const [localPreview, setLocalPreview] = useState<string | null>(previewUrl ?? null)

    const handleFile = async (file: File | null) => {
      if (!file || isAnalyzing) return
      if (!file.type.startsWith('image/')) {
        toast.error(t('howIEat.photoLog.invalidFile'))
        return
      }

      const objectUrl = URL.createObjectURL(file)
      setLocalPreview(objectUrl)
      setIsAnalyzing(true)

      try {
        const result = await howIEatService.analyzeFoodPhoto(file, locale)
        const items = mealItemsFromPhotoAnalysis(result)
        onAnalyzed(items, result.notes, objectUrl)
        const confidence =
          result.overallConfidence != null
            ? t('howIEat.photoLog.confidence', { pct: String(Math.round(result.overallConfidence * 100)) })
            : null
        toast.success(confidence ? `${t('howIEat.photoLog.success')} ${confidence}` : t('howIEat.photoLog.success'))
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message)
            : t('howIEat.photoLog.analyzeFailed')
        toast.error(msg || t('howIEat.photoLog.analyzeFailed'))
        setLocalPreview(null)
        URL.revokeObjectURL(objectUrl)
      } finally {
        setIsAnalyzing(false)
      }
    }

    useImperativeHandle(ref, () => ({
      openFilePicker: () => inputRef.current?.click(),
    }))

    return (
      <>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null
            void handleFile(file)
            e.target.value = ''
          }}
        />

        {!hidden && (
          <div
            className={`bg-[#1a1a1a] border border-emerald-500/25 rounded-xl ${compact ? 'p-3 mb-3' : 'p-4 mb-6'}`}
          >
            {!compact && (
              <>
                <p className="text-white font-medium text-sm mb-1">{t('howIEat.photoLog.title')}</p>
                <p className="text-white/55 text-xs mb-3 leading-relaxed">{t('howIEat.photoLog.subtitle')}</p>
              </>
            )}

            {localPreview && (
              <div className={`mb-3 rounded-lg overflow-hidden border border-white/10 ${compact ? 'max-h-32' : 'max-h-40'}`}>
                <img src={localPreview} alt="" className={`w-full object-cover ${compact ? 'h-32' : 'h-40'}`} />
              </div>
            )}

            <button
              type="button"
              disabled={isAnalyzing}
              onClick={() => inputRef.current?.click()}
              className="w-full py-3 rounded-xl bg-emerald-600/90 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isAnalyzing ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('howIEat.photoLog.analyzing')}
                </>
              ) : (
                t('howIEat.photoLog.uploadPhoto')
              )}
            </button>

            {!compact && (
              <p className="text-white/40 text-[11px] mt-3 leading-relaxed">{t('howIEat.photoLog.disclaimer')}</p>
            )}
          </div>
        )}
      </>
    )
  },
)

FoodPhotoLogSection.displayName = 'FoodPhotoLogSection'

export default FoodPhotoLogSection
